import type { Snapshot } from 'ts-components/PiChat/protocol';
import type { PiChatFrameMeta } from 'ts-components/PiChat/usePiChat';
import type { TreeFile } from 'ts-components/PiChat/fileTree/tree';
import { httpOriginFromWsUrl } from 'ts-components/PiChat/wsOrigin';

export const fetchVersionFiles = async (
  filesUrl: string,
  wsUrl: string,
  signal: AbortSignal
): Promise<TreeFile[]> => {
  const response = await fetch(
    new URL(filesUrl, httpOriginFromWsUrl(wsUrl) ?? undefined),
    { signal }
  );
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const snapshot: Snapshot = await response.json();
  return [
    ...snapshot.files,
    ...(snapshot.blobs ?? []).map(({ path }) => ({ path })),
  ];
};

export interface VersionFilesDeps {
  onLoaded: (snapshotId: string, files: TreeFile[]) => void;
  onError: (error: unknown) => void;
}

export const requestVersionFiles = (
  refs: { snapshotId: string; filesUrl: string }[],
  meta: Pick<PiChatFrameMeta, 'url' | 'signal'>,
  requested: Set<string>,
  { onLoaded, onError }: VersionFilesDeps
): Promise<void>[] =>
  refs.flatMap(({ snapshotId, filesUrl }) => {
    if (requested.has(snapshotId)) return [];
    requested.add(snapshotId);
    return [
      fetchVersionFiles(filesUrl, meta.url, meta.signal).then(
        (files) => {
          if (meta.signal.aborted) {
            requested.delete(snapshotId);
            return;
          }
          onLoaded(snapshotId, files);
        },
        (error) => {
          requested.delete(snapshotId);
          if (!meta.signal.aborted) onError(error);
        }
      ),
    ];
  });
